import React from "react";
import ReactDOM from "react-dom";

export interface WalkthroughGenerationModalState {
  phase: "checking" | "generating" | "success" | "error";
  approachName: string;
  problemTitle?: string;
  message?: string;
  timeElapsed?: number;
}

interface WalkthroughGenerationModalProps {
  isOpen: boolean;
  onClose: () => void;
  state: WalkthroughGenerationModalState;
}

export const WalkthroughGenerationModal: React.FC<
  WalkthroughGenerationModalProps
> = ({ isOpen, onClose, state }) => {
  // Debug logging
  React.useEffect(() => {
    console.log("🚶 Walkthrough Modal state changed:", {
      isOpen,
      phase: state.phase,
      approachName: state.approachName,
    });
  }, [isOpen, state.phase, state.approachName]);

  const getModalContent = () => {
    switch (state.phase) {
      case "checking":
        return {
          icon: (
            <div className="w-8 h-8 mx-auto mb-3 text-blue-600">
              <svg
                className="w-8 h-8 animate-spin"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
          ),
          title: "Checking for Walkthrough",
          message: `Looking for existing walkthrough for "${state.approachName}"`,
          subtitle: "This will only take a moment...",
          bgColor: "bg-white",
          borderColor: "border-blue-300",
          textColor: "text-gray-700",
          iconColor: "text-blue-600",
        };

      case "generating":
        return {
          icon: (
            <div className="w-8 h-8 mx-auto mb-3 text-blue-600">
              <svg
                className="w-8 h-8 animate-spin"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
            </div>
          ),
          title: "Generating Algorithm Walkthrough",
          message: `Creating step-by-step walkthrough for "${state.approachName}"${state.problemTitle ? ` on ${state.problemTitle}` : ""}`,
          subtitle:
            "AI is analyzing the algorithm and creating detailed execution steps... (10-30 seconds)",
          bgColor: "bg-white",
          borderColor: "border-blue-300",
          textColor: "text-gray-700",
          iconColor: "text-blue-600",
        };

      case "success":
        return {
          icon: (
            <div className="w-8 h-8 mx-auto mb-3 text-green-600">
              <svg
                className="w-8 h-8"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
          ),
          title: "Walkthrough Ready! 📝",
          message: `Step-by-step walkthrough for "${state.approachName}" has been generated`,
          subtitle: "You can now see the detailed execution table below",
          bgColor: "bg-white",
          borderColor: "border-green-300",
          textColor: "text-gray-700",
          iconColor: "text-green-600",
        };

      case "error":
        return {
          icon: (
            <div className="w-8 h-8 mx-auto mb-3 text-red-600">
              <svg
                className="w-8 h-8"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </div>
          ),
          title: "Walkthrough Generation Failed",
          message: state.message || "Failed to generate walkthrough",
          subtitle: "Please try again or check your connection",
          bgColor: "bg-white",
          borderColor: "border-red-300",
          textColor: "text-gray-700",
          iconColor: "text-red-600",
        };

      default:
        return null;
    }
  };

  const showCloseButton = state.phase === "success" || state.phase === "error";
  const autoCloseDelay = state.phase === "success" ? 3000 : 0;

  // Auto-close success modal after 3 seconds
  React.useEffect(() => {
    if (autoCloseDelay > 0) {
      const timer = setTimeout(() => {
        onClose();
      }, autoCloseDelay);
      return () => clearTimeout(timer);
    }
  }, [autoCloseDelay, onClose]);

  if (!isOpen) return null;

  const content = getModalContent();
  if (!content) return null;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-gray-900 bg-opacity-10 flex items-center justify-center z-50">
      <div
        className={`bg-white rounded-lg shadow-2xl border-4 ${content.borderColor} p-6 max-w-md w-full mx-4`}
        style={{
          boxShadow:
            "0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(0, 0, 0, 0.05)",
        }}
      >
        {/* Header */}
        <div className="flex justify-between items-start mb-4">
          <div className="flex-1">
            {content.icon}
            <h3
              className={`text-base font-semibold text-center ${content.textColor}`}
            >
              {content.title}
            </h3>
          </div>
          {showCloseButton && (
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 p-1 rounded-md hover:bg-gray-100 transition-colors ml-2"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          )}
        </div>

        {/* Content */}
        <div className="text-center">
          <p className={`text-sm ${content.textColor} mb-2 leading-relaxed`}>
            {content.message}
          </p>
          <p className={`text-xs text-gray-500`}>{content.subtitle}</p>

          {/* Time elapsed for generation */}
          {state.phase === "generating" && state.timeElapsed && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <p className="text-xs text-gray-600">
                Time elapsed: {Math.floor(state.timeElapsed / 1000)}s
              </p>
              {state.timeElapsed > 10000 && (
                <p className="text-xs text-gray-500 mt-1">
                  Almost done... Creating detailed execution steps
                </p>
              )}
            </div>
          )}

          {/* Progress bar for generation */}
          {state.phase === "generating" && (
            <div className="mt-4">
              <div className="w-full bg-gray-200 rounded-full h-1.5">
                <div
                  className="bg-blue-600 h-1.5 rounded-full transition-all duration-1000 ease-out"
                  style={{
                    width: state.timeElapsed
                      ? `${Math.min(90, (state.timeElapsed / 20000) * 100)}%`
                      : "15%",
                  }}
                ></div>
              </div>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        {state.phase === "error" && (
          <div className="mt-6 flex justify-center">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-600 text-white text-sm rounded-md hover:bg-gray-700 transition-colors"
            >
              Close
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};
